// src/router/routeWrappers.tsx
import React from 'react';
import { useParams, useNavigate, Navigate, NavigateFunction } from 'react-router-dom';

/**
 * Contract every add/edit screen must satisfy.
 *
 *   id === undefined  →  "New" mode  (blank form)
 *   id === number     →  "Edit" mode (load existing record)
 */
export interface CrudProps {
  id?: number;
  onNavigate: NavigateFunction;
}

/**
 * Factory that produces { New, Edit } route components for any CRUD screen.
 *
 * @param Component   - The screen component (must implement CrudProps)
 * @param fallbackPath - Where to redirect if :id in the URL is not a valid number
 *
 * Example
 * -------
 *   const Letterhead = createCrudRoutes(SchoolLetterHeader, '/letterheads');
 *   const Student    = createCrudRoutes(StudentForm,        '/students');
 *
 *   <Route path="letterheads/new"       element={<Letterhead.New />} />
 *   <Route path="letterheads/edit/:id"  element={<Letterhead.Edit />} />
 */
export function createCrudRoutes(
  // Accept any component whose props are exactly CrudProps (or a superset).
  // Using React.ComponentType<CrudProps> avoids the generic-subtype TS error
  // while still enforcing that the component accepts id + onNavigate.
  Component: React.ComponentType<CrudProps>,
  fallbackPath: string,
) {
  // ── /some-path/new ──────────────────────────────────────────────────────────
  const New: React.FC = () => {
    const navigate = useNavigate();
    return <Component key="new" onNavigate={navigate} />;
  };
  New.displayName = `${Component.displayName ?? Component.name}New`;

  // ── /some-path/edit/:id ─────────────────────────────────────────────────────
  const Edit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const numId = parseInt(id ?? '', 10);

    // Guard: non-numeric :id → redirect to the list
    if (isNaN(numId)) return <Navigate to={fallbackPath} replace />;

    // key={`edit-${numId}`} forces a remount whenever the id changes,
    // so navigating edit/3 → edit/7 never leaks stale state.
    return <Component key={`edit-${numId}`} id={numId} onNavigate={navigate} />;
  };
  Edit.displayName = `${Component.displayName ?? Component.name}Edit`;

  return { New, Edit };
}